import React, { useState } from "react";
import { Modal, Field, Input, Button } from "@/components/ui-kit";
import { DatePicker } from "@/components/DatePicker";
import { PackagePlus, Upload, Camera } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/** Iter v34f (#5) — Barang Masuk form (auto-approve) per stock item. */
export function StockInModal({ stock, onClose, onDone }) {
  const [form, setForm] = useState({
    quantity: "", entry_date: new Date().toISOString().slice(0, 10),
    sj_number: "", supplier: "", note: "",
    sj_photo_b64: "", sj_photo_filename: "", sj_photo_mime: "",
  });
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5_000_000) return toast.error("File maksimal 5MB");
    try {
      const b64 = await fileToBase64(f);
      setForm((p) => ({
        ...p,
        sj_photo_b64: b64,
        sj_photo_filename: f.name,
        sj_photo_mime: f.type || "application/octet-stream",
      }));
      toast.success("Foto dipilih: " + f.name);
    } catch { toast.error("Gagal baca foto"); }
  };

  const submit = async () => {
    const qty = parseFloat(form.quantity);
    if (!qty || qty <= 0) return toast.error("Jumlah barang harus > 0");
    if (!form.entry_date) return toast.error("Tanggal masuk wajib diisi");
    setBusy(true);
    try {
      const r = await api.post(`/stocks/${stock.id}/stock-in`, { ...form, quantity: qty });
      toast.success(`Stok bertambah → ${r.data.new_quantity} ${stock.unit}`);
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal catat barang masuk");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={!!stock} onClose={onClose} title="Barang Masuk" size="md">
      <div className="space-y-4" data-testid="stock-in-form">
        <div className="bg-emerald-50 border border-emerald-200 p-3 text-xs">
          <PackagePlus className="h-4 w-4 inline mr-1 text-emerald-700" />
          <b>{stock?.material_name}</b> {stock?.code && <>· <span className="font-mono text-stone-500">{stock.code}</span></>}
          <div className="text-stone-700 mt-1">Saldo saat ini: <b>{stock?.quantity || 0} {stock?.unit}</b></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Jumlah Masuk *">
            <Input type="number" min={0} step="any" value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              placeholder="0" data-testid="stock-in-qty" />
          </Field>
          {/* Iter v34cu (#3) — Tanggal stok masuk, tercatat di Log Stok. */}
          <Field label="Tanggal Masuk *">
            <DatePicker value={form.entry_date}
              onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
              data-testid="stock-in-date" />
          </Field>
          <Field label="Nomor Surat Jalan">
            <Input value={form.sj_number} onChange={(e) => setForm({ ...form, sj_number: e.target.value })}
              placeholder="SJ/2026/00123" data-testid="stock-in-sj-number" />
          </Field>
          <Field label="Supplier / Asal Barang">
            <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              placeholder="contoh: PT Sumber Material" data-testid="stock-in-supplier" />
          </Field>
        </div>
        <Field label="Catatan">
          <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="w-full border border-stone-300 px-3 py-2 text-sm" data-testid="stock-in-note" />
        </Field>
        <Field label="Foto Surat Jalan / Nota (gambar, ≤5MB)">
          <label className="flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50" data-testid="stock-in-photo-label">
            <Upload className="h-3.5 w-3.5" />
            <span className="text-xs">{form.sj_photo_filename || "Pilih foto (galeri/kamera)"}</span>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="stock-in-photo" />
          </label>
          {form.sj_photo_b64 && (
            <img src={`data:${form.sj_photo_mime};base64,${form.sj_photo_b64}`}
              alt="Preview" className="mt-2 max-h-40 border border-stone-200" />
          )}
        </Field>
        <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button variant="primary" onClick={submit} disabled={busy} data-testid="stock-in-submit">
            <PackagePlus className="h-3.5 w-3.5" /> {busy ? "Memproses..." : "Catat Barang Masuk"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
